
import { Lead, LeadStatus, EmailWithType } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { placeDetails } from './detailsService';
import { getMultiplePhotoUrls } from './photoService';
import { saveBulkLeadImages, deleteAllLeadImages, setPrimaryImage } from '@/services/leadImagesService';
import { toast } from '@/hooks/use-toast';

// Helper: extract emails from website or place details text
function extractEmailsFromText(text: string): string[] {
  if (!text) return [];
  // Regex for basic email matching
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  return text.match(emailRegex) || [];
}

// Helper: format emails as EmailWithType[] if more than one, or string if one
function formatExtractedEmails(emails: string[]): string | EmailWithType | EmailWithType[] {
  if (emails.length === 0) return '';
  if (emails.length === 1) return emails[0];
  // Mark first as primary, others as secondary
  return emails.map((email, idx) => ({
    type: idx === 0 ? 'primary' : 'secondary',
    email,
  }) as EmailWithType);
}

export const refreshPlaceData = async (lead: Lead): Promise<Lead | null> => {
  if (!lead.placeId) {
    console.error('No place ID available for refresh');
    return null;
  }

  try {
    const details = await placeDetails(lead.placeId);

    if (details.status !== 'OK' || !details.result) {
      throw new Error('Failed to fetch updated place details');
    }

    const placeData = details.result;

    // Extract emails from available data
    // Try: website, address, name, etc.
    let foundEmails: string[] = [];
    if (placeData.website) {
      foundEmails = extractEmailsFromText(placeData.website);
    }
    // If not found in website, try name, address, phone (uncommon, but fallback just in case)
    if (foundEmails.length === 0 && placeData.name) {
      foundEmails = extractEmailsFromText(placeData.name);
    }
    if (foundEmails.length === 0 && placeData.formatted_address) {
      foundEmails = extractEmailsFromText(placeData.formatted_address);
    }
    // Optionally, more properties can be checked here.

    // Make sure the emails are formatted for storage
    const formattedEmail = formatExtractedEmails(foundEmails);

    // Always fetch fresh Google photos when available
    let newImageUrls: string[] = [];
    if (placeData.photos && placeData.photos.length > 0) {
      // Delete existing images first to avoid duplicates
      if (lead.images.length > 0) {
        await deleteAllLeadImages(lead.id);
      }
      newImageUrls = await getMultiplePhotoUrls(placeData.photos, 800, 10);
    }

    // Update lead data, including email (always cast to json for storage)
    const { data: updatedLead, error: updateError } = await supabase
      .from('leads')
      .update({
        name: placeData.name,
        address: placeData.formatted_address || placeData.vicinity || lead.address,
        phone: placeData.international_phone_number || placeData.formatted_phone_number,
        website: placeData.website || lead.website,
        rating: placeData.rating,
        review_count: placeData.user_ratings_total,
        latitude: placeData.geometry?.location?.lat || lead.latitude,
        longitude: placeData.geometry?.location?.lng || lead.longitude,
        updated_at: new Date().toISOString(),
        // Save extracted email (fallback to existing if not found)
        email: foundEmails.length > 0 ? formattedEmail : lead.email
      })
      .eq('id', lead.id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    // If we got new images, save them and set the first as primary
    if (newImageUrls.length > 0) {
      const savedImages = await saveBulkLeadImages(lead.id, newImageUrls);
      // Set the first image as primary and update lead.image_url
      if (savedImages.length > 0) {
        await setPrimaryImage(savedImages[0].id, lead.id);
      }
    }

    // Convert raw database email to our expected format (handles string/object/array)
    let formattedResultEmail: string | EmailWithType | EmailWithType[] = '';
    if (updatedLead.email) {
      if (typeof updatedLead.email === 'string') {
        formattedResultEmail = updatedLead.email;
      } else if (Array.isArray(updatedLead.email)) {
        formattedResultEmail = updatedLead.email as EmailWithType[];
      } else if (typeof updatedLead.email === 'object') {
        formattedResultEmail = updatedLead.email as EmailWithType;
      }
    }

    return {
      ...lead,
      name: updatedLead.name,
      address: updatedLead.address || '',
      phone: updatedLead.phone || '',
      website: updatedLead.website || '',
      email: formattedResultEmail,
      rating: updatedLead.rating || 0,
      reviewCount: updatedLead.review_count || 0,
      status: (updatedLead.status || lead.status) as LeadStatus,
      images: newImageUrls.length > 0
        ? newImageUrls.map((url, index) => ({
          id: '',
          leadId: lead.id,
          imageUrl: url,
          isPrimary: index === 0,
          position: index
        }))
        : [] // Clear images array since we deleted them if no new images were found
    };
  } catch (error) {
    console.error('Error refreshing place data:', error);
    toast({
      title: "Refresh Failed",
      description: "Failed to update business information",
      variant: "destructive",
    });
    return null;
  }
};
